import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import { apiClient } from '../apiClient';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

const UserContext = createContext(null);

const adminPermissions = {
    invoice: { canUpload: true, canViewScheduled: true, canDeleteScheduled: true, canViewHistory: true },
    customer: { canUpload: true, canDelete: true },
    settings: { canViewSubscription: true, canChangePlan: true, canViewBillwave: true, canUpdateBillwave: true },
    customerInfo: { canViewPaymentMethods: true, canUpdatePaymentMethods: true, canViewCustomerInfo: true, canUpdateCustomerInfo: true },
    billing: { canViewHistory: true },
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionVersion, setSessionVersion] = useState(null);

    const fetchUser = useCallback(async () => {
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
            try {
                const tokenResult = await firebaseUser.getIdTokenResult();
                if (tokenResult.claims.systemadmin) {
                    let systemAdminRole = 'admin';
                    let systemAdminStatus = 'active';

                    try {
                        const adminDoc = await getDoc(doc(db, 'adminUsers', firebaseUser.uid));
                        if (adminDoc.exists()) {
                            const adminData = adminDoc.data();
                            systemAdminRole = adminData.role || 'admin';
                            systemAdminStatus = adminData.status || 'active';
                        }
                    } catch (e) {
                        console.error("Error fetching adminUser doc:", e);
                    }

                    if (systemAdminStatus === 'disabled') {
                        auth.signOut();
                        setUser(null);
                        return;
                    }

                    setUser({
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        displayName: firebaseUser.displayName || '',
                        systemadmin: true,
                        systemAdminRole: systemAdminRole,
                        systemAdminStatus: systemAdminStatus,
                        role: 'systemadmin',
                        permissions: adminPermissions,
                    });
                } else {
                    const response = await apiClient.get('/v1/user/me');
                    if (response.ok) {
                        const userData = await response.json();
                        setUser(userData);
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                setUser(null);
            }
        } else {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            setLoading(true); 
            if (firebaseUser) {
                try {
                    const maintDoc = await getDoc(doc(db, 'systemSettings', 'maintenanceConfig'));
                    if (maintDoc.exists()) {
                        const currentVersion = maintDoc.data().maintenanceVersion || 0;
                        setSessionVersion(currentVersion);
                    } else {
                        setSessionVersion(0);
                    }
                } catch (error) {
                    console.error("Error fetching initial maintenance version:", error);
                    setSessionVersion(0);
                }
            } else {
                setSessionVersion(null);
            }
            await fetchUser();
            setLoading(false);
        });
        return () => unsubscribe();
    }, [fetchUser]);

    // Monitor maintenance version for forced logout
    useEffect(() => {
        if (!user || user.systemadmin) return;

        const maintDocRef = doc(db, 'systemSettings', 'maintenanceConfig');
        const unsubscribe = onSnapshot(maintDocRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.data();
                const currentVersion = data.maintenanceVersion || 0;
                
                const now = new Date();
                const startAt = data.startAt?.toDate();
                const endAt = data.endAt?.toDate();
                const isMaintenanceActive = data.enabled && startAt && endAt && now >= startAt && now < endAt;

                if (isMaintenanceActive && sessionVersion !== null && currentVersion > sessionVersion) {
                    console.log('Maintenance version mismatch and maintenance is active. Forced logout triggered.');
                    auth.signOut();
                }
            }
        }, (error) => {
            console.error("Error monitoring maintenance config:", error);
        });

        return () => unsubscribe();
    }, [user, sessionVersion]);

    const refetchUser = useCallback(async () => {
        setLoading(true);
        await fetchUser();
        setLoading(false);
    }, [fetchUser]);

    const value = {
        user,
        loading,
        isAdmin: user?.systemadmin || user?.role === 'admin' || user?.role === 'owner',
        systemAdminRole: user?.systemAdminRole || 'admin',
        permissions: user?.permissions || {},
        refetchUser,
        hasPermission: (permissionPath) => {
            if (!user || !user.permissions) return false;
            const [category, key] = permissionPath.split('.');
            if (!category || !key) return false;
            return user.permissions[category]?.[key] === true;
        },
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
